import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { AffectedMembersDialog } from '@/pages/affected-members-dialog'
import { MemberSelfCancellationSetting } from '@/pages/member-self-cancellation-setting'
import { useLevelRenewalOverrides } from '@/pages/level-renewal-overrides-api'
import {
  GraceChangeImpact,
  RenewalSettingsPanel,
  type LevelRenewalSettingsDraft,
} from '@/pages/renewal-settings-support'

export interface ReminderTouch {
  id?: string
  offset: number
  kind: 'reminder' | 'final_warning'
  draftKey?: number
}

export interface RenewalSettings {
  renewal_invoice_lead_days: number
  renewal_grace_days: number
  renewal_auto_lapse_enabled: boolean
  renewal_reminder_touches: ReminderTouch[]
  member_self_cancellation_enabled?: boolean
}

interface RenewalSettingsResponse {
  renewal_settings: RenewalSettings
}

interface RenewalArmingCountResponse {
  renewal_arming: { overdue_members: unknown[] }
}

export function RenewalSettingsPage({ onReviewArming }: { onReviewArming?: () => void }) {
  const queryClient = useQueryClient()
  const [graceDays, setGraceDays] = React.useState('')
  const [autoLapseEnabled, setAutoLapseEnabled] = React.useState(false)
  const [touches, setTouches] = React.useState<ReminderTouch[]>([])
  const [membersOpen, setMembersOpen] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [levelDrafts, setLevelDrafts] = React.useState<Record<number, LevelRenewalSettingsDraft>>(
    {},
  )
  const settingsQuery = useQuery({
    queryKey: ['renewal-settings'],
    queryFn: () => getJSON<RenewalSettingsResponse>('/api/v1/renewal_settings'),
    retry: false,
  })
  const updateSettings = useMutation({
    mutationFn: ({
      confirmGraceLapses,
      impactToken,
    }: {
      confirmGraceLapses: boolean
      impactToken?: string
    }) =>
      patchJSON<RenewalSettingsResponse>('/api/v1/renewal_settings', {
        renewal_settings: {
          renewal_grace_days: graceDays,
          confirm_grace_lapses: confirmGraceLapses,
          grace_change_impact_token: impactToken,
          ...(autoLapseEnabled === false && { renewal_auto_lapse_enabled: false }),
          renewal_reminder_touches: touches.map(({ id, offset }) => ({
            ...(id && { id }),
            offset,
          })),
        },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['renewal-settings'], data)
      syncSettings(data.renewal_settings)
    },
  })
  const { levels, updateLevels } = useLevelRenewalOverrides()
  const armingQuery = useRenewalArming(settingsQuery.data?.renewal_settings)

  const syncSettings = React.useCallback((settings: RenewalSettings) => {
    setGraceDays(settings.renewal_grace_days.toString())
    setAutoLapseEnabled(settings.renewal_auto_lapse_enabled)
    setTouches(settings.renewal_reminder_touches)
  }, [])

  React.useEffect(() => {
    if (settingsQuery.data) syncSettings(settingsQuery.data.renewal_settings)
  }, [settingsQuery.data, syncSettings])

  const changeSettings = () => {
    setSaved(false)
    updateSettings.reset()
  }

  async function saveChanges() {
    await updateSettings.mutateAsync({
      confirmGraceLapses: impact !== null,
      impactToken: impact?.confirmation_token,
    })
    if (Object.keys(levelDrafts).length > 0) {
      await updateLevels.mutateAsync(levelDrafts)
      setLevelDrafts({})
    }
    setSaved(true)
  }

  const fieldErrors = apiFieldMessages(updateSettings.error, 'renewal_grace_days')
  const scheduleErrors = apiFieldMessages(updateSettings.error, 'renewal_reminder_touches')
  const levelErrors = levelRenewalErrors(updateLevels.error)
  const impact = graceChangeImpact(updateSettings.error)

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <RenewalSettingsHeader
        disabled={!settingsQuery.data || updateSettings.isPending || updateLevels.isPending}
        saving={updateSettings.isPending || updateLevels.isPending}
        onSave={() => void saveChanges().catch(() => undefined)}
      />

      {settingsQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading renewal settings...
        </p>
      ) : settingsQuery.isError ? (
        <Alert>Renewal settings unavailable</Alert>
      ) : settingsQuery.data ? (
        <RenewalSettingsContent
          armingCount={armingQuery.data?.renewal_arming.overdue_members.length}
          autoLapseEnabled={autoLapseEnabled}
          fieldErrors={fieldErrors}
          graceDays={graceDays}
          impact={impact}
          levels={levels}
          levelDrafts={levelDrafts}
          levelErrors={levelErrors}
          onChangeSettings={changeSettings}
          onLevelDraftChange={(level, draft) => {
            setSaved(false)
            updateLevels.reset()
            setLevelDrafts((current) => ({ ...current, [level.id]: draft }))
          }}
          onReviewArming={onReviewArming}
          onSetAutoLapseEnabled={setAutoLapseEnabled}
          onSetGraceDays={setGraceDays}
          onShowAffectedMembers={() => setMembersOpen(true)}
          onSetTouches={setTouches}
          saved={saved}
          scheduleErrors={scheduleErrors}
          settings={settingsQuery.data.renewal_settings}
          touches={touches}
        />
      ) : null}

      {settingsQuery.data?.renewal_settings.member_self_cancellation_enabled !== undefined && (
        <MemberSelfCancellationSetting
          initialEnabled={settingsQuery.data.renewal_settings.member_self_cancellation_enabled}
        />
      )}

      <AffectedMembersDialog impact={impact} open={membersOpen} onOpenChange={setMembersOpen} />
    </div>
  )
}

function RenewalSettingsContent({
  onChangeSettings,
  onLevelDraftChange,
  onSetAutoLapseEnabled,
  onSetGraceDays,
  onSetTouches,
  ...props
}: {
  onChangeSettings: () => void
  onLevelDraftChange: (
    level: import('@/pages/MembershipLevelsPage.types').MembershipLevel,
    draft: LevelRenewalSettingsDraft,
  ) => void
  onSetAutoLapseEnabled: (enabled: boolean) => void
  onSetGraceDays: (days: string) => void
  onSetTouches: (touches: ReminderTouch[]) => void
} & Omit<
  React.ComponentProps<typeof RenewalSettingsPanel>,
  'onGraceDaysChange' | 'onLevelDraftChange' | 'onSetAutoLapseEnabled' | 'onTouchesChange'
>) {
  return (
    <Card className="max-w-2xl">
      <RenewalSettingsPanel
        {...props}
        onGraceDaysChange={(value) => {
          onChangeSettings()
          onSetGraceDays(value)
        }}
        onLevelDraftChange={onLevelDraftChange}
        onSetAutoLapseEnabled={(enabled) => {
          onChangeSettings()
          onSetAutoLapseEnabled(enabled)
        }}
        onTouchesChange={(touches) => {
          onChangeSettings()
          onSetTouches(touches)
        }}
      />
    </Card>
  )
}

function RenewalSettingsHeader({
  disabled,
  saving,
  onSave,
}: {
  disabled: boolean
  saving: boolean
  onSave: () => void
}) {
  return (
    <header className="flex items-center justify-between pb-4">
      <h1 className="text-heading-sm font-semibold text-foreground">Renewal settings</h1>
      <Button type="button" disabled={disabled} onClick={onSave}>
        {saving ? 'Saving...' : 'Save changes'}
      </Button>
    </header>
  )
}

function graceChangeImpact(error: Error | null): GraceChangeImpact | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const body = error.body
  if (typeof body !== 'object' || body === null || !('grace_change_impact' in body)) return null
  const impact = (body as { grace_change_impact: GraceChangeImpact }).grace_change_impact
  if (
    typeof impact?.count !== 'number' ||
    !Array.isArray(impact.members) ||
    typeof impact.confirmation_token !== 'string'
  )
    return null
  return impact
}

function useRenewalArming(settings: RenewalSettings | undefined) {
  return useQuery({
    queryKey: ['renewal-arming'],
    queryFn: () => getJSON<RenewalArmingCountResponse>('/api/v1/renewal_arming'),
    enabled: settings?.renewal_auto_lapse_enabled === false,
    retry: false,
  })
}

function levelRenewalErrors(error: Error | null) {
  return [
    ...apiFieldMessages(error, 'renewal_grace_days_override'),
    ...apiFieldMessages(error, 'renewal_reminder_touches_override'),
  ]
}
